import { Table } from 'dexie';
import { AppDatabase, IndexedDbFactory } from '../factories/indexeddb-provider.factory';
import { ValidationError } from '@app/domain/entities';
import { IndexedDbOperationError } from '../../errors';

type DeepPartial<T> = {
  [P in keyof T]?: T[P] | undefined;
};

export abstract class IndexedDbRepositoryBase {
  constructor(
    protected readonly indexedDbFactory: IndexedDbFactory,
    protected readonly storeName: string,
  ) {}

  protected getDb(): Promise<AppDatabase> {
    return this.indexedDbFactory.getDb();
  }

  protected getTable<T = unknown>(db: AppDatabase, storeName: string = this.storeName): Table<T, string> {
    return db[storeName as keyof AppDatabase] as unknown as Table<T, string>;
  }

  protected executeStoreOperation<T>(
    operation: string,
    context: Record<string, unknown>,
    action: (db: AppDatabase) => Promise<T>,
    targetStore: string = this.storeName,
  ): Promise<T> {
    return this.runOperation(targetStore, operation, context, action);
  }

  protected executeTransaction<T>(
    operation: string,
    context: Record<string, unknown>,
    getTables: (db: AppDatabase) => Table[],
    action: (db: AppDatabase) => Promise<T>,
  ): Promise<T> {
    return this.runOperation(this.storeName, operation, context, async (db) => {
      const tables = getTables(db);
      return db.transaction('rw', tables, () => action(db));
    });
  }

  protected async deleteKeys(store: { delete(key: string): Promise<unknown> }, keys: readonly string[]): Promise<number> {
    let deletedCount = 0;

    for (const key of keys) {
      await store.delete(key);
      deletedCount += 1;
    }

    return deletedCount;
  }

  private async runOperation<T>(
    targetStore: string,
    operation: string,
    context: Record<string, unknown>,
    action: (db: AppDatabase) => Promise<T>,
  ): Promise<T> {
    try {
      const db = await this.getDb();
      return await action(db);
    } catch (error) {
      this.indexedDbFactory.noteOperationFailure(error);

      throw new IndexedDbOperationError(
        `${targetStore}.${operation}`,
        {
          storeName: targetStore,
          ...context,
          diagnostics: this.indexedDbFactory.getDiagnostics(),
          ...this.describeError(error),
        },
        error,
      );
    }
  }

  private describeError(error: unknown): Record<string, unknown> {
    if (error instanceof Error) {
      return {
        causeName: error.name,
        causeMessage: error.message,
      };
    }

    return {
      causeName: typeof error,
      causeMessage: String(error),
    };
  }
}

export abstract class BaseIndexDbRepository<TResult extends { id: string }> extends IndexedDbRepositoryBase {
  async findAll(): Promise<TResult[]> {
    return this.executeStoreOperation('findAll', {}, async (db) => this.getTable<TResult>(db).toArray());
  }

  async getById(id: string): Promise<TResult | null> {
    return this.executeStoreOperation('getById', { id }, async (db) => {
      const result = await this.getTable<TResult>(db).get(id);
      return result ?? null;
    });
  }

  async create(entity: TResult): Promise<void> {
    await this.executeStoreOperation('create', { id: entity.id }, async (db) => {
      await this.getTable<TResult>(db).put(entity);
    });
  }

  async update(id: string, payload: DeepPartial<Omit<TResult, 'id'>>): Promise<void> {
    const data = await this.getById(id);
    if (!data) throw new ValidationError('id', `Item with id ${id} can not be found`);

    const clean = Object.fromEntries(
      Object.entries({ ...payload, id: undefined }).filter(([, value]) => value !== undefined),
    );

    Object.assign(data, clean);

    await this.executeStoreOperation('update', { id }, async (db) => {
      await this.getTable<TResult>(db).put(data);
    });
  }

  async delete(id: string): Promise<void> {
    await this.executeStoreOperation('delete', { id }, async (db) => {
      await this.getTable(db).delete(id);
    });
  }

  async clear(): Promise<void> {
    await this.executeStoreOperation('clear', {}, async (db) => {
      await this.getTable(db).clear();
    });
  }
}
